/**
 * 勾选状态管理 Hook
 * 处理树节点的勾选状态，包括父子联动
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import React from 'react';
import { TreeNodesStates, CheckedKeysObj, OnCheckInfo, DisabledTreeNode, CheckKeysInfo } from '../iTree';
import { handleCheckState, getCheck, getStrictlyValue } from '../utils/util';

interface UseCheckStateProps {
    defaultCheckedKeys?: string[] | CheckedKeysObj;
    checkedKeys?: string[] | CheckedKeysObj;
    checkStrictly?: boolean;
    checkable?: boolean | null;
    expandedKeys?: string[];
    onCheck?: (checkedKeys: string[] | { checked: string[]; halfChecked: string[] }, info: OnCheckInfo) => void;
    children?: React.ReactNode;
    treeData?: any[];
    lazyLoad?: boolean;
    disabled?: boolean;
    controlled?: boolean;
    /**
     * 父子不关联(checkStrictly=true)时，按住 Shift 连选且父节点处于收起状态：
     * - false：仅包含父节点本身（默认行为）
     * - true：父节点及其子孙节点都会被视为在同一连选区间内
     */
    strictRangeHasFolded?: boolean;
}

interface UseCheckStateReturn {
    checkedKeys: string[];
    halfCheckedKeys: string[];
    treeNodesStates: TreeNodesStates;
    checkKeys: CheckKeysInfo | undefined;
    handleCheck: (treeNode: React.ReactElement, event?: React.MouseEvent | React.KeyboardEvent) => void;
    setCheckedKeys: React.Dispatch<React.SetStateAction<string[]>>;
    getCheckedKeys: (keys?: string[]) => string[];
    initTreeNodesStates: (treeChildren: React.ReactElement[], startIndex?: number) => void;
}

/**
 * 勾选状态管理 Hook
 */
export function useCheckState(props: UseCheckStateProps): UseCheckStateReturn {
    const {
        // defaultCheckedKeys = [],
        checkedKeys: controlledCheckedKeys,
        checkStrictly = false,
        checkable = false,
        onCheck,
        children,
        // treeData,
        lazyLoad = false,
        // disabled = false,
        controlled
    } = props;

    // 状态管理
    const [checkedKeys, setCheckedKeys] = useState<string[]>(() => {
        return getDefaultCheckedKeys(props);
    });
    const [halfCheckedKeys, setHalfCheckedKeys] = useState<string[]>([]);
    const [isInitialized, setIsInitialized] = useState<boolean>(false);

    // refs
    const treeNodesStatesRef = useRef<TreeNodesStates>({});
    const lazyLoadTreeNodesStatesRef = useRef<TreeNodesStates>({});
    const checkKeysRef = useRef<CheckKeysInfo | undefined>(undefined);
    const _checkedKeysRef = useRef<string[] | undefined>([]);
    const disabledTreeNodesRef = useRef<DisabledTreeNode[]>([]);
    const lastCheckedKeyRef = useRef<string | null>(null);
    // const checkedKeysChangeRef = useRef<boolean>(true);

    // 同步受控的 checkedKeys
    useEffect(() => {
        if (controlledCheckedKeys !== undefined) {
            const keys = getCheckedKeysFromProp(controlledCheckedKeys, checkStrictly);
            setCheckedKeys(keys);
            // checkedKeysChangeRef.current = true;
        }
    }, [controlledCheckedKeys, checkStrictly]);

    /**
     * 获取默认勾选的keys
     */
    function getDefaultCheckedKeys(props: UseCheckStateProps): string[] {
        const { defaultCheckedKeys, checkedKeys, checkStrictly } = props;

        if (controlled && checkedKeys) {
            return getCheckedKeysFromProp(checkedKeys, checkStrictly);
        }

        if (defaultCheckedKeys) {
            return getCheckedKeysFromProp(defaultCheckedKeys, checkStrictly);
        }

        return [];
    }

    /**
     * 从prop中获取checkedKeys
     */
    function getCheckedKeysFromProp(
        keys: string[] | CheckedKeysObj | undefined,
        checkStrictly?: boolean
    ): string[] {
        if (!keys) return [];

        if (checkStrictly) {
            if ((keys as CheckedKeysObj)?.checked) {
                return (keys as CheckedKeysObj).checked;
            }
            if (!Array.isArray(keys)) {
                return [];
            }
        }

        return Array.isArray(keys) ? keys : [];
    }

    /**
     * 初始化树节点状态
     * 初始加载 & checkedKeys发生改变 联动更新checkedKeys
     */
    const initTreeNodesStates = useCallback((treeChildren: React.ReactElement[], startIndex: number = 0) => {
        const treeNodesStates: TreeNodesStates = {};
        const checkedPositions: string[] = [];

        // ✅ 性能优化：预先创建 Set，将查找复杂度从 O(m) 降低到 O(1)
        const checkedKeysSet = new Set(checkedKeys);

        loopAllChildren(treeChildren, (item: React.ReactElement, _index: number, pos: string, keyOrPos: string, siblingPosition: any) => {
            treeNodesStates[pos] = {
                node: item,
                key: keyOrPos,
                checked: false,
                halfChecked: false,
                siblingPosition,
            };

            // ✅ 性能优化：使用 Set.has() 替代 indexOf
            if (checkedKeysSet.has(keyOrPos)) {
                treeNodesStates[pos].checked = true;
                checkedPositions.push(pos);
            }

            // 处理disabled节点
            if (item.props?.disabled) {
                disabledTreeNodesRef.current.push({ key: keyOrPos });
            }
        }, undefined, startIndex);

        // 处理父子联动
        if (!checkStrictly) {
            if (checkedPositions.length > 0) {
                handleCheckState(treeNodesStates, checkedPositions, true, []);
                const checkKeys = getCheck(treeNodesStates);
                setHalfCheckedKeys(checkKeys.halfCheckedKeys);
                checkKeysRef.current = checkKeys;
                _checkedKeysRef.current = getCheckedKeys(checkKeys.checkedKeys);

                // 非严格模式下，首次初始化时更新checkedKeys为计算后的结果
                if (!controlled && !isInitialized) {
                    setCheckedKeys(checkKeys.checkedKeys);
                    setIsInitialized(true);
                }
            } else {
                // ✅ 修复：当 checkedPositions 为空时，清空半选状态
                setHalfCheckedKeys([]);
                checkKeysRef.current = {
                    checkedKeys: [],
                    halfCheckedKeys: [],
                    checkedNodes: [],
                    checkedNodesPositions: [],
                    treeNodesStates
                };
                _checkedKeysRef.current = [];
            }
        }

        if (!isInitialized) {
            setIsInitialized(true);
        }

        if (lazyLoad) {
            lazyLoadTreeNodesStatesRef.current = treeNodesStates;
        } else {
            treeNodesStatesRef.current = treeNodesStates;
        }
    }, [checkedKeys, checkStrictly, lazyLoad, controlled, isInitialized]);

    /**
     * 获取去除disabled节点后的checkedKeys
     */
    const getCheckedKeys = useCallback((keys: string[] = checkedKeys): string[] => {
        let resultKeys = [...keys];

        for (let i = 0; i < disabledTreeNodesRef.current.length; i++) {
            const key = disabledTreeNodesRef.current[i].key;
            const checked = disabledTreeNodesRef.current[i].checked;

            if (typeof checked !== 'boolean') {
                continue;
            }

            if (!checked && resultKeys.includes(key)) {
                const index = resultKeys.indexOf(key);
                resultKeys.splice(index, 1);
            } else if (checked && !resultKeys.includes(key)) {
                resultKeys.push(key);
            }
        }

        return resultKeys;
    }, [checkedKeys]);

    /**
     * 处理节点勾选
     */
    const handleCheck = useCallback((treeNode: React.ReactElement, event?: React.MouseEvent | React.KeyboardEvent) => {
        let checked = !treeNode.props.checked;

        if (treeNode.props.halfChecked) {
            checked = true;
        }

        const key = treeNode.props.eventKey;
        let newCheckedKeys = [...checkedKeys];
        const index = newCheckedKeys.indexOf(key);

        const newSt: OnCheckInfo = {
            event: 'check',
            node: treeNode,
            checked,
            eventObject: event
        };

        let treeNodesStates = lazyLoad ? lazyLoadTreeNodesStatesRef.current : treeNodesStatesRef.current;


        const anchorKey = lastCheckedKeyRef.current;
        const canRangeSelect = Boolean(checkable && event?.shiftKey && anchorKey && anchorKey !== key && Object.keys(treeNodesStates).length);

        if (canRangeSelect) {
            // includeCollapsedDescendants 含义：
            // - 非严格模式：始终包含折叠父节点的所有子孙（父子关联）
            // - 严格模式：由 strictRangeHasFolded 决定
            const includeCollapsedDescendants = !checkStrictly || !!props.strictRangeHasFolded;
            const rangeNodes = getRangeNodesBetweenKeys(
                treeNodesStates,
                anchorKey as string,
                key,
                includeCollapsedDescendants,
                props.expandedKeys
            );

            if (rangeNodes.length > 1) {
                if (checkStrictly) {
                    // 父子不关联：仅更新勾选数组
                    const checkedSet = new Set(newCheckedKeys);

                    rangeNodes.forEach(({ key: rangeKey, pos, node }) => {
                        if (isTreeNodeDisabled(node)) {
                            return;
                        }

                        const state = treeNodesStates[pos];
                        if (state) {
                            state.checked = checked;
                            state.halfChecked = false;
                        }

                        if (checked && !checkedSet.has(rangeKey)) {
                            checkedSet.add(rangeKey);
                            newCheckedKeys.push(rangeKey);
                        }
                        if (!checked && checkedSet.has(rangeKey)) {
                            checkedSet.delete(rangeKey);
                            const removeIndex = newCheckedKeys.indexOf(rangeKey);
                            if (removeIndex > -1) {
                                newCheckedKeys.splice(removeIndex, 1);
                            }
                        }
                    });

                    newSt.checkedNodes = collectCheckedNodesFromStates(treeNodesStates, newCheckedKeys);

                    if (!controlled) {
                        setCheckedKeys([...newCheckedKeys]);
                    }

                    const halfChecked: string[] = controlledCheckedKeys
                        ? (controlledCheckedKeys as CheckedKeysObj).halfChecked
                        : [];
                    onCheck?.(getStrictlyValue(newCheckedKeys, halfChecked), newSt);
                    lastCheckedKeyRef.current = key;
                    return;
                } else {
                    // 父子关联：批量处理 positions
                    const affectedPositions: string[] = [];
                    rangeNodes.forEach(({ pos, node }) => {
                        if (isTreeNodeDisabled(node)) return;
                        affectedPositions.push(pos);
                        const state = treeNodesStates[pos];
                        if (state) {
                            state.checked = checked;
                            state.halfChecked = false;
                        }
                    });

                    if (affectedPositions.length > 1) {
                        handleCheckState(treeNodesStates, affectedPositions, checked, []);
                        const checkKeys = getCheck(treeNodesStates);
                        newSt.checkedNodes = checkKeys.checkedNodes;
                        newSt.checkedNodesPositions = checkKeys.checkedNodesPositions;
                        newSt.halfCheckedKeys = checkKeys.halfCheckedKeys;
                        newSt.checkedChildrenKeys = checkKeys.checkedChildrenKeys;

                        checkKeysRef.current = checkKeys;
                        newCheckedKeys = getCheckedKeys(checkKeys.checkedKeys);
                        setHalfCheckedKeys(checkKeys.halfCheckedKeys);

                        if (!controlled) {
                            newCheckedKeys = checkKeys.checkedKeys;
                            setCheckedKeys(newCheckedKeys);
                        } else {
                            newCheckedKeys = checkKeys.checkedKeys;
                        }

                        onCheck?.(newCheckedKeys, newSt);
                        lastCheckedKeyRef.current = key;
                        return;
                    }
                }
            }
        }

        if (checkStrictly) {
            // 严格模式：父子节点不联动
            if (checked && index === -1) {
                newCheckedKeys.push(key);
            }
            if (!checked && index > -1) {
                newCheckedKeys.splice(index, 1);
            }

            // 更新节点状态
            const nodePos = treeNode.props.pos;
            if (treeNodesStates[nodePos]) {
                treeNodesStates[nodePos].checked = checked;
            }

            // ✅ P0-优化2：严格模式下，直接从 treeNodesStates 收集 checkedNodes，避免全树递归
            newSt.checkedNodes = [];
            const newCheckedKeysSet = new Set(newCheckedKeys);

            // 优先从 treeNodesStates 收集（更快）
            if (Object.keys(treeNodesStates).length > 0) {
                for (const pos in treeNodesStates) {
                    const nodeState = treeNodesStates[pos];
                    if (nodeState && nodeState.key && newCheckedKeysSet.has(nodeState.key)) {
                        (newSt.checkedNodes as React.ReactNode[]).push(nodeState.node);
                        if (newSt.checkedNodes.length === newCheckedKeys.length) {
                            break;
                        }
                    }
                }
            } else if (children) {
                // 降级方案：treeNodesStates 未初始化时，走原逻辑
                loopAllChildren(children as React.ReactElement[], (item: React.ReactElement, _ind: number, _pos: string, keyOrPos: string) => {
                    if (newCheckedKeysSet.has(keyOrPos)) {
                        (newSt.checkedNodes as React.ReactNode[]).push(item);
                    }
                });
            }

            if (!controlled) {
                setCheckedKeys(newCheckedKeys);
            }

            const halfChecked: string[] = controlledCheckedKeys
                ? (controlledCheckedKeys as CheckedKeysObj).halfChecked
                : [];

            // 传递 getStrictlyValue 格式的结果
            const strictlyResult = getStrictlyValue(newCheckedKeys, halfChecked)
            onCheck?.(strictlyResult, newSt);
        } else {
            // 非严格模式：父子节点联动
            // 如果 treeNodesStates 为空，需要初始化
            if (Object.keys(treeNodesStates).length === 0 && children) {
                initTreeNodesStates(children as React.ReactElement[], 0);
            }

            let realPos = treeNode.props.pos;

            // 在lazyLoad模式下，找到真实的pos
            if (lazyLoad) {
                for (let i in treeNodesStates) {
                    if (treeNodesStates[i] && treeNodesStates[i].key === key) {
                        realPos = i;
                        break;
                    }
                }
            }

            // 确保 treeNodesStates[realPos] 存在
            if (!treeNodesStates[realPos]) {
                treeNodesStates[realPos] = {
                    node: treeNode,
                    key: key,
                    checked: false,
                    halfChecked: false,
                    siblingPosition: {}
                };
            }

            if (checked && index === -1) {
                treeNodesStates[realPos].checked = true;
                const checkedPositions: string[] = [];
                Object.keys(treeNodesStates).forEach(i => {
                    if (treeNodesStates[i].checked) {
                        checkedPositions.push(i);
                    }
                });
                handleCheckState(treeNodesStates, checkedPositions, true, []);
            }

            if (!checked) {
                if (treeNodesStates[realPos]) {
                    treeNodesStates[realPos].checked = false;
                    treeNodesStates[realPos].halfChecked = false;
                }
                handleCheckState(treeNodesStates, [realPos], false, []);
            }

            const checkKeys = getCheck(treeNodesStates);
            newSt.checkedNodes = checkKeys.checkedNodes;
            newSt.checkedNodesPositions = checkKeys.checkedNodesPositions;
            newSt.halfCheckedKeys = checkKeys.halfCheckedKeys;
            newSt.checkedChildrenKeys = checkKeys.checkedChildrenKeys;

            // checkKeysRef.current = checkKeys;
            newCheckedKeys = getCheckedKeys(checkKeys.checkedKeys);
            setHalfCheckedKeys(checkKeys.halfCheckedKeys);

            if (!controlled) {
                // 直接使用计算后的 checkedKeys
                newCheckedKeys = checkKeys.checkedKeys;
                setCheckedKeys(newCheckedKeys);
            } else {
                // 受控模式下也使用计算后的 checkedKeys
                newCheckedKeys = checkKeys.checkedKeys;
            }

            onCheck?.(newCheckedKeys, newSt);
        }

        lastCheckedKeyRef.current = key;
    }, [checkedKeys, checkStrictly, lazyLoad, props, onCheck, children]);

    return {
        checkedKeys: !checkStrictly ? (_checkedKeysRef.current || []) : checkedKeys, // 联动模式统一从_checkedKeysRef.current获取
        // checkedKeys,
        halfCheckedKeys,
        treeNodesStates: lazyLoad ? lazyLoadTreeNodesStatesRef.current : treeNodesStatesRef.current,
        checkKeys: checkKeysRef.current,
        handleCheck,
        setCheckedKeys,
        getCheckedKeys,
        initTreeNodesStates
    };
}

// 辅助函数
function loopAllChildren(childs: React.ReactElement[], callback: Function, parent?: any, baseNum?: number | string): void {
    baseNum === undefined && (baseNum = 0);
    const loop = (children: React.ReactElement[], level: string | number, _parent?: any, baseNum?: number | string) => {
        const len = React.Children.count(children);
        React.Children.forEach(children, (item: React.ReactElement, index) => {
            if (item) {
                const pos = `${level}-${index + (typeof baseNum === 'string' ? parseInt(baseNum) : baseNum as number)}`;
                const siblingPosition = getSiblingPosition(index, len);
                callback(item, index, pos, item.key || pos, siblingPosition, _parent);
                if (item.props?.children) {
                    loop(item.props.children, pos, { node: item, pos }, 0);
                }
            }
        });
    };
    loop(childs, 0, parent, baseNum);
}

function getSiblingPosition(index: number, len: number): any {
    const siblingPosition: any = {};
    if (len === 1) {
        siblingPosition.first = true;
        siblingPosition.last = true;
    } else {
        siblingPosition.first = index === 0;
        siblingPosition.last = index === len - 1;
    }
    return siblingPosition;
}

interface RangeNodeInfo {
    key: string;
    pos: string;
    node: React.ReactElement;
}

/**
 * 获取两个指定键之间的树节点范围
 *
 * @param treeNodesStates 树节点状态对象，包含所有节点的位置和状态信息
 * @param startKey 起始节点的键
 * @param endKey 结束节点的键
 * @param includeCollapsedDescendants 是否包含已折叠的后代节点，如果为false则跳过已折叠节点的后代
 * @param expandedKeys 当前展开的节点 key 列表，用于判断节点是否为折叠状态
 * @returns 包含起始键和结束键之间所有节点的数组，按位置顺序排列
 */
function getRangeNodesBetweenKeys(
    treeNodesStates: TreeNodesStates,
    startKey: string,
    endKey: string,
    includeCollapsedDescendants: boolean,
    expandedKeys?: string[]
): RangeNodeInfo[] {
    const positions = Object.keys(treeNodesStates)
    const collapsedStack: string[] = [];
    const nodes: RangeNodeInfo[] = [];

    positions.forEach(pos => {
        const state = treeNodesStates[pos];
        if (!state || !state.key) {
            return;
        }

        // 清理已经离开折叠分支的父节点
        while (collapsedStack.length && !isDescendantPos(pos, collapsedStack[collapsedStack.length - 1])) {
            collapsedStack.pop();
        }

        if (collapsedStack.length && isDescendantPos(pos, collapsedStack[collapsedStack.length - 1])) {
            return;
        }

        nodes.push({ key: state.key as string, pos, node: state.node });

        // 标记折叠节点：不关联 & 有子节点但是为折叠状态
        if (!includeCollapsedDescendants && hasCollapsedChildren(state.node, expandedKeys)) {
            collapsedStack.push(pos);
        }
    });

    const startIndex = nodes.findIndex(item => item.key === startKey);
    const endIndex = nodes.findIndex(item => item.key === endKey);

    if (startIndex === -1 || endIndex === -1) {
        return [];
    }

    const [from, to] = startIndex <= endIndex ? [startIndex, endIndex] : [endIndex, startIndex];
    return nodes.slice(from, to + 1);
}


function isDescendantPos(pos: string, parentPos: string): boolean {
    return pos.startsWith(`${parentPos}-`);
}

function hasCollapsedChildren(node: React.ReactElement, expandedKeys?: string[]): boolean {
    if (!node?.props) return false;
    const children = node.props.children;
    const hasChildren = Boolean(children) && React.Children.count(children) > 0;
    if (!hasChildren) {
        return false;
    }
    // 没有 key 时无法根据 expandedKeys 判断，保守认为未折叠
    if (!node.key) {
        return false;
    }
    if (expandedKeys && Array.isArray(expandedKeys)) {
        // 不在 expandedKeys 中视为折叠
        return !expandedKeys.includes(node.key);
    }
    return false;
}

function isTreeNodeDisabled(node: React.ReactElement): boolean {
    return Boolean(node?.props?.disabled);
}

function collectCheckedNodesFromStates(treeNodesStates: TreeNodesStates, checkedKeys: string[]): React.ReactNode[] {
    const targetSet = new Set(checkedKeys);
    const nodes: React.ReactNode[] = [];

    for (const pos in treeNodesStates) {
        const state = treeNodesStates[pos];
        if (state && state.key && targetSet.has(state.key as string)) {
            nodes.push(state.node);
            if (nodes.length === targetSet.size) {
                break;
            }
        }
    }

    return nodes;
}